import {Link} from 'react-router-dom';
import {ArrowUpRight, Car, Drop, ShieldCheck, Sparkle, Wrench} from '@phosphor-icons/react';
import type {Icon} from '@phosphor-icons/react';
import {formatMoney} from '@/lib/money';
import {tenantPath} from '@/lib/tenant/resolve';
import type {ApiService} from '@/types/api';

/** 90 мин -> «1,5 ч», 1440 мин -> «1 д»: коротко, как в образце. */
export function formatDuration(minutes: number): string {
  const m = Math.round(minutes);
  if (m >= 1440) {
    const days = m / 1440;
    return `${Number.isInteger(days) ? days : days.toFixed(1).replace('.', ',')} д`;
  }
  if (m >= 60) {
    const hours = m / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1).replace('.', ',')} ч`;
  }
  return `${m} мин`;
}

/** Значок услуги по ключевым словам названия: на компьютере показывается слева, как в образце. */
export function serviceIcon(name: string): Icon {
  const n = name.toLowerCase();
  if (/мойк/.test(n)) return Drop;
  if (/салон|химчист|озон/.test(n)) return Car;
  if (/полиров|фар|диск|блеск/.test(n)) return Sparkle;
  if (/керамик|плёнк|пленк|защит|покрыт/.test(n)) return ShieldCheck;
  return Wrench;
}

/**
 * compact — строка на главной: название, длительность, «Выбрать».
 * full — страница услуг: длительность и описание под названием.
 * При наведении фон и рамка плавно проявляются, строка приподнимается на 1 px.
 */
export function ServiceRow({
  slug,
  service,
  locale,
  variant = 'compact',
}: {
  slug: string;
  service: ApiService;
  locale: string;
  variant?: 'compact' | 'full';
}) {
  const duration = formatDuration(service.durationMinutes);
  const ServiceIcon = serviceIcon(service.name);
  return (
    <Link
      to={`${tenantPath(slug, 'book')}?service=${service.id}`}
      className="service-row flex items-center gap-3 rounded-2xl border border-transparent px-4 py-5 lg:gap-4"
    >
      <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-foreground lg:flex">
        <ServiceIcon size={22} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-[17px] font-semibold leading-snug text-foreground">{service.name}</span>
        <span className="text-[15px] leading-snug text-muted-foreground">
          {variant === 'full' && service.description ? `${duration} — ${service.description}` : duration}
        </span>
      </span>
      <span className="flex flex-col items-end gap-1">
        <span className="text-[17px] font-semibold text-foreground">
          {formatMoney(service.priceMinor, service.currency, locale)}
        </span>
        {variant === 'compact' ? <span className="text-[15px] text-muted-foreground">Выбрать</span> : null}
      </span>
      <ArrowUpRight size={22} className="shrink-0 text-foreground" aria-hidden />
    </Link>
  );
}
